'use client';

import { AppConfigSchema } from '@utazzo/shared';

import type { AppConfig } from '@utazzo/shared';

/**
 * Fetches the AppConfig from the API and validates the response shape.
 *
 * Uses a plain `fetch` (no auth) because `/config` is a public boot endpoint.
 * We don't go through the admin's `apiClient` because this module is shared
 * with apps/website which has no axios + auth store coupling.
 *
 * `apiBaseUrl` is expected to already include the versioned API prefix
 * (e.g. `http://localhost:3000/api/v1`) - same convention as the rest of
 * the app's API calls. We only append the controller path `/config`, so the
 * resolved URL is `http://localhost:3000/api/v1/config`.
 *
 * If the server returns a payload that doesn't match the schema, we throw -
 * better to fail loudly than render with a half-broken config.
 */
export const fetchAppConfig = async (apiBaseUrl: string): Promise<AppConfig> => {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/config`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`AppConfig fetch failed: ${response.status} ${response.statusText}`);
  }
  const data: unknown = await response.json();
  return AppConfigSchema.parse(data);
};
